/* eslint-disable react-hooks/set-state-in-effect */
import { useEffect, useState } from "react";
import { api, formatApiError, spriteUrl } from "@/lib/api";
import { toast } from "sonner";
import { X, ArrowLeftRight } from "lucide-react";

function shinySprite(pid, isShiny) {
  return isShiny
    ? `https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/pokemon/shiny/${pid}.png`
    : spriteUrl(pid);
}

export default function TradeModal({ open, onClose, onTraded }) {
  const [trainers, setTrainers] = useState([]);
  const [aId, setAId] = useState("");
  const [bId, setBId] = useState("");
  const [slotA, setSlotA] = useState(null);
  const [slotB, setSlotB] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setAId(""); setBId(""); setSlotA(null); setSlotB(null);
    api.get("/trainers").then(({ data }) => setTrainers(data)).catch(() => {});
  }, [open]);

  if (!open) return null;

  const trA = trainers.find(t => t.id === aId);
  const trB = trainers.find(t => t.id === bId);
  const canTrade = trA && trB && slotA != null && slotB != null && aId !== bId;

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/trades", {
        trainer_a_id: aId, slot_a: slotA, trainer_b_id: bId, slot_b: slotB,
      });
      const evoNote = (data.evolved_a || data.evolved_b)
        ? " · une évolution s'est produite !" : "";
      toast.success(`Échange réussi${evoNote}`);
      onTraded?.();
      onClose();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <div
      className="fixed inset-0 flex items-center justify-center p-4 overflow-auto"
      style={{ background: "rgba(26,26,26,0.55)", zIndex: 70 }}
      onClick={onClose}
      data-testid="trade-modal"
    >
      <div className="panel-strong p-5 max-w-3xl w-full my-4" onClick={(e) => e.stopPropagation()}>
        <button className="absolute top-3 right-3 btn-ink p-1 px-1.5" onClick={onClose}>
          <X size={14}/>
        </button>
        <div className="overline">Salle d&apos;échange</div>
        <h2 className="font-serif-display text-3xl leading-none mt-1 mb-4">Échanger entre dresseurs</h2>
        <p className="text-xs text-[#4A4A4A] font-mono mb-4">
          Les Pokémon qui évoluent par échange (Kadabra, Machopeur, Gravalanch, Spectrum)
          se transformeront automatiquement après le swap.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[
            { label: "Dresseur A", id: aId, setId: setAId, slot: slotA, setSlot: setSlotA, exclude: bId, otherKey: "a" },
            { label: "Dresseur B", id: bId, setId: setBId, slot: slotB, setSlot: setSlotB, exclude: aId, otherKey: "b" },
          ].map((side) => {
            const tr = trainers.find(t => t.id === side.id);
            return (
              <div key={side.otherKey} className="panel p-3" data-testid={`trade-side-${side.otherKey}`}>
                <div className="overline mb-2">{side.label}</div>
                <select
                  className="ink-input text-[12px] py-1"
                  data-testid={`trade-select-${side.otherKey}`}
                  value={side.id}
                  onChange={(e) => { side.setId(e.target.value); side.setSlot(null); }}
                >
                  <option value="">— Choisir —</option>
                  {trainers.filter(t => t.id !== side.exclude).map((t) => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </select>
                {tr && (
                  <div className="mt-3">
                    <div className="overline text-[9px] mb-1">Équipe ({tr.team?.length || 0}/6)</div>
                    {(!tr.team || tr.team.length === 0) ? (
                      <div className="text-xs font-mono text-[#7A7A7A]">Aucun Pokémon</div>
                    ) : (
                      <div className="grid grid-cols-3 gap-1">
                        {tr.team.map((p, i) => (
                          <button
                            key={i}
                            data-testid={`trade-${side.otherKey}-slot-${i}`}
                            onClick={() => side.setSlot(i)}
                            className={`panel p-1 text-center ${side.slot === i ? "bg-[#1A1A1A] text-[#F4EFE6] border-[#1A1A1A]" : "hover:bg-[#EAE4D9]"}`}
                          >
                            <img src={shinySprite(p.pokemon_id, p.is_shiny)} alt={p.name} className="pixelated w-10 h-10 mx-auto"/>
                            <div className="text-[10px] font-mono truncate">{p.name}</div>
                            <div className="text-[9px]">Lv{p.level}</div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="mt-5 flex justify-center">
          <button
            data-testid="trade-confirm-btn"
            className="btn-ink btn-leaf"
            disabled={!canTrade || busy}
            onClick={submit}
          >
            <ArrowLeftRight size={14}/> {busy ? "Échange..." : "Procéder à l'échange"}
          </button>
        </div>
      </div>
    </div>
  );
}
